import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      {/* NAV */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: scrolled ? 'rgba(44, 44, 46, 0.98)' : 'rgba(44, 44, 46, 0.95)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(196, 145, 90, 0.1)',
        boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.15)' : 'none',
        transition: 'all 0.3s ease',
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 72,
        }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 12 }} onClick={closeMenu}>
            <div style={{
              width: 40,
              height: 40,
              background: 'var(--amber)',
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--font-heading)',
              fontSize: 22,
              color: 'var(--charcoal)',
              fontWeight: 700,
            }}>B</div>
            <div>
              <span style={{ fontFamily: 'var(--font-heading)', fontSize: '1.1rem', color: 'var(--white)', display: 'block' }}>
                Bantock Accounting
              </span>
              <span style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.65rem',
                textTransform: 'uppercase',
                letterSpacing: '0.15em',
                color: 'var(--amber-light)',
                marginTop: -2,
                display: 'block',
              }}>& Advisory Ltd</span>
            </div>
          </Link>

          {/* Desktop nav */}
          <ul className="nav-links" style={{
            display: 'flex',
            gap: 32,
            listStyle: 'none',
            alignItems: 'center',
          }}>
            <li><a href="/#services" style={navLinkStyle}>Services</a></li>
            <li><a href="/#about" style={navLinkStyle}>About Us</a></li>
            <li><a href="/#testimonials" style={navLinkStyle}>Testimonials</a></li>
            <li>
              <a href="/#contact" style={{
                ...navLinkStyle,
                background: 'var(--amber)',
                color: 'var(--white)',
                padding: '8px 20px',
                borderRadius: 6,
                fontWeight: 600,
              }}>Get in Touch</a>
            </li>
          </ul>

          {/* Mobile toggle */}
          <button
            className="nav-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
            style={{
              display: 'none',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 8,
              flexDirection: 'column',
              gap: 5,
            }}
          >
            <span style={{ display: 'block', width: 24, height: 2, background: 'var(--white)', borderRadius: 2 }} />
            <span style={{ display: 'block', width: 24, height: 2, background: 'var(--white)', borderRadius: 2 }} />
            <span style={{ display: 'block', width: 24, height: 2, background: 'var(--white)', borderRadius: 2 }} />
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="mobile-menu" style={{
            background: 'var(--charcoal)',
            padding: 24,
            borderBottom: '1px solid rgba(196,145,90,0.1)',
          }}>
            <a href="/#services" onClick={closeMenu} style={mobileLinkStyle}>Services</a>
            <a href="/#about" onClick={closeMenu} style={mobileLinkStyle}>About Us</a>
            <a href="/#testimonials" onClick={closeMenu} style={mobileLinkStyle}>Testimonials</a>
            <a href="/#contact" onClick={closeMenu} style={{
              ...mobileLinkStyle,
              background: 'var(--amber)',
              color: 'var(--white)',
              padding: '10px 20px',
              borderRadius: 6,
              fontWeight: 600,
              textAlign: 'center',
              marginTop: 8,
            }}>Get in Touch</a>
          </div>
        )}
      </nav>

      {/* MAIN */}
      <main>{children}</main>

      {/* FOOTER */}
      <footer style={{
        background: 'var(--charcoal)',
        padding: '48px 24px 32px',
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 24,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36,
              height: 36,
              background: 'var(--amber)',
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--font-heading)',
              fontSize: 18,
              color: 'var(--charcoal)',
              fontWeight: 700,
            }}>B</div>
            <span style={{ fontFamily: 'var(--font-heading)', color: 'var(--white)', fontSize: '0.95rem' }}>
              Bantock Accounting &amp; Advisory Ltd
            </span>
          </div>

          <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)' }}>
            &copy; {new Date().getFullYear()} Bantock Accounting and Advisory Ltd. All rights reserved.
          </p>

          <ul style={{ display: 'flex', gap: 24, listStyle: 'none' }}>
            <li><a href="/#services" style={footerLinkStyle}>Services</a></li>
            <li><a href="/#about" style={footerLinkStyle}>About</a></li>
            <li><a href="/#contact" style={footerLinkStyle}>Contact</a></li>
          </ul>
        </div>
      </footer>

      {/* RESPONSIVE CSS */}
      <style>{`
        @media (max-width: 640px) {
          .nav-links { display: none !important; }
          .nav-toggle { display: flex !important; }
        }
      `}</style>
    </>
  );
};

const navLinkStyle: React.CSSProperties = {
  color: 'rgba(255,255,255,0.7)',
  fontSize: '0.875rem',
  fontWeight: 500,
  letterSpacing: '0.03em',
  transition: 'color 0.3s ease',
};

const mobileLinkStyle: React.CSSProperties = {
  display: 'block',
  color: 'rgba(255,255,255,0.75)',
  fontSize: '0.95rem',
  fontWeight: 500,
  padding: '10px 0',
};

const footerLinkStyle: React.CSSProperties = {
  color: 'rgba(255,255,255,0.5)',
  fontSize: '0.825rem',
  transition: 'color 0.3s ease',
};

export default Layout;